import React, { useEffect, useState, useRef, useCallback } from 'react'
import { connect } from 'react-redux'

import LgyField from 'app/components/util/lgy-field/LgyField'
import LgyAlertDialog from 'app/components/util/lgy-alert-dialog/LgyAlertDialog'
import BasicLgySelectField from 'app/components/util/lgy-select-field/BasicLgySelectField'
import BasicLgyInputField from 'app/components/util/lgy-input-field/BasicLgyInputField'
import { uploaderInit, removeFile, startUpload, setOption, destroy } from '../actions/pluploadAction'
import { getCorrespondenceList } from '../actions/correspondenceActions'
import { getCookieValue } from 'app/actions/genericActionCreator'
import { getDocType } from '../util/oversightUtils'

const OversightDocumentUpload = ({ uploadState, uploaderInit, removeFile, setOption, startUpload, destroy, auditId, auditUnit, user, getCorrespondenceList }) => {

  const browseButtonRef = useRef(0)
  const [docType, setDocType] = useState({ code: '', name: '' })
  const [correspondenceFileName, setCorrespondenceFileName] = useState('')
  const [alertValues, setAlertValues] = useState({ type: '', heading: '', text: '' })
  const [errors, setErrors] = useState({})

  const resetDocumentUploads = useCallback(
    () => {
      setDocType({ code: '', name: '' })
      setCorrespondenceFileName('')
      setErrors({})
    }, [])

  useEffect(() => {
    const options = {
      runtimes: 'html5,html4',
      browse_button: browseButtonRef.current,
      chunk_size: '14mb',
      container: 'container',
      max_file_size: '1.5gb',
      multi_selection: false,
      max_retries: '5',
      url: `/lgyhub/api/audits/correspondence`,
      headers: {
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      },
      filters: [
        { title: 'Allowed files', extensions: 'pdf,doc,docx,xlsx,xls,tif,tiff,gif,jpg,jpeg,png,mp4,zip,mp3,aac,flac,ogg,wma' }
      ]
    }

    uploaderInit(options)

    return () => {
      resetDocumentUploads()
      destroy()
    }
  }, [destroy, uploaderInit, resetDocumentUploads])

  useEffect(() => {
    if (uploadState.files && uploadState.files.length > 1) {
      removeFile(uploadState.files[0].id)
    }
  }, [uploadState.files, removeFile])

  useEffect(() => {
    if (uploadState.error) {
      setAlertValues({ type: '', heading: '', text: '' })
      if (uploadState.files[0]) {
      removeFile(uploadState.files[0].id)
      }
      if (uploadState.error === 'File size error.') {
        setErrors({ fileError: 'File size must be greater than 0 KB and less than 1.5 GB.' })
      } else if (uploadState.error === 'File extension error.') {
        setErrors({ fileError: 'Only pdf, doc, docx, xlsx, xls, tif, tiff, gif, jpg, jpeg, png, mp4, zip, mp3, aac, flac, ogg, and wma file extensions are supported.' })
      }
    }
  }, [uploadState.files, uploadState.error, removeFile])

  useEffect(() => {
    if (uploadState.total && uploadState.total.failed === 1) {
      setAlertValues({ type: 'error', heading: 'Upload Failed', text: `Failed to upload ${uploadState.files[0].name}` })
      resetDocumentUploads()
    } else if (uploadState.total && uploadState.total.uploaded === 1) {
      setAlertValues({ type: 'success', heading: 'Upload Success', text: `Uploaded ${uploadState.files[0].name}` })
      getCorrespondenceList({auditId: auditId, correspondenceId: null})
      resetDocumentUploads()
    }
  }, [uploadState.files, uploadState.total, auditId, getCorrespondenceList, resetDocumentUploads]) 

  const handleInvalid = (event) => {
    event.preventDefault()
    setErrors({ ...errors, [event.target.id]: 'Required' })
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setAlertValues({ type: '', heading: '', text: '' })
    setOption({
      option: 'multipart_params', value: {
        auditId: auditId,
        viewableToMonitoringUnit: auditUnit === 'M' ? true : false,
        viewableToQualityAssurance: auditUnit === 'Q' ? true : false,
        viewableToThirdParty: user.authorizations.includes('ROLE_VA Third Party Oversight') ? true : false,
        viewableToExternal: user.authorizations.includes('ROLE_External Oversight User') ? true : false,
        documentTypeCode: docType.code,
        documentTypeName: docType.name,
        uniqueId: uploadState.id+new Date(),
        correspondenceFileName: correspondenceFileName
      }
    })
    startUpload()
  }

  const handleFileChange = () => {
    setAlertValues({ type: '', heading: '', text: '' })
    setErrors({})
  }

  const handleChange = (event) => {
    setErrors({})
    if (event.target.id === 'docType') {
      setDocType({ code: event.target.value, name: getDocType(event.target.value) })
    }
    if (event.target.id === 'correspondenceFileName') {
      setCorrespondenceFileName(event.target.value)
    }
  }

  return <form className='oversight-document-upload' onSubmit={handleSubmit}>
    {alertValues.type && <LgyAlertDialog type={alertValues.type} heading={alertValues.heading} text={alertValues.text} />}
    <BasicLgySelectField label='Document Type' id='docType' required value={docType.code} onInvalid={handleInvalid} error={errors.docType}
      onChange={handleChange}>
      <option value='OS1'>Loan Files</option>
      <option value='OS2'>QC Information</option>
      <option value='OS3'>Marketing Materials</option>
      <option value='OS4'>Questionnaire</option>
      <option value='OS5'>Underwriter Information</option>
      <option value='OS6'>Company Information</option>
      <option value='OS7'>RLC Data</option>
      <option value='OS8'>Other</option>
    </BasicLgySelectField>
    <BasicLgyInputField id='correspondenceFileName' label='Document Name' value={correspondenceFileName} maxLength='100' required
      onChange={handleChange} onInvalid={handleInvalid} error={errors.correspondenceFileName} />
    <br />
      <label><div id='container'>
      <button
        id='browse-button'
        className='usa-button oversight-upload-button'
        type='button'
        ref={browseButtonRef}
        disabled={uploadState.state === 2}
        onClick={handleFileChange}>Browse...</button>
      <button
        className='usa-button oversight-upload-button'
        type='submit'
        disabled={!uploadState.files || uploadState.files.length < 1 || uploadState.state === 2 || Object.keys(errors).length > 0}>
        {uploadState.state === 2 ? `${uploadState.total.percent}% complete` : 'Upload File'}
      </button>
    </div></label>
    {errors.fileError && <span className='usa-input-error-message' id='browse-button-error' role='alert'>{errors.fileError}</span>}
    <LgyField label='Selected File' value={uploadState.files && uploadState.files.length > 0 ?
      uploadState.files[0].name : 'Please select a file.'} />
  </form>
}

const mapStateToProps = state => ({
  uploadState: state.oversight.uploader,
  user: state.currentUser
})

const mapDispatchToProps = { uploaderInit, removeFile, startUpload, setOption, destroy, getCorrespondenceList }

export default connect(mapStateToProps, mapDispatchToProps)(OversightDocumentUpload)
